import { useGridApiRef } from '@mui/x-data-grid-pro';
import { POST_UNREALISED_FX_GAINS_SUCCESS } from 'adminConstants/routes';
import Button from 'components/Button';
import ButtonGroup from 'components/ButtonGroup';
import InteractiveTable, { Column } from 'components/InteractiveTable';
import { Row } from 'components/Layout';
import PendingDraftEqualisationsModal from 'components/PendingDraftEqualisationsModal';
import ReviewPayments from 'components/ReviewPayments';
import Spacing from 'components/Spacing';
import TransactionAllocator from 'components/TransactionAllocator';
import { useLayoutContext } from 'contexts/LayoutContext';
import {
  CurrencyRateDetails,
  TransactionOperation,
} from 'further-types/lp/transaction';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { useDisclosure, usePagination } from 'further-ui/hooks';
import { ActionRefLabels } from 'further-ui/labels';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import { useEffect, useState } from 'react';
import PayablesOrReceivablesHistory from './PayablesOrReceivablesHistory';

const pageId = 'post-unrealised-fx-gains';

type Props = {
  vehicleId: string;
  viewDate: CalendarDay;
  currencyRateOverrides: Map<CurrencyCode, CurrencyRateDetails>;
  onCurrenciesLoaded: (
    currencies: Array<[CurrencyCode, CurrencyRateDetails]>,
  ) => void;
  onLastPostingDateLoaded: (date: CalendarDay | null) => void;
};

const PayablesOrReceivables: React.FC<Props> = ({
  vehicleId,
  viewDate,
  currencyRateOverrides,
  onCurrenciesLoaded,
  onLastPostingDateLoaded,
}) => {
  const { vehicle } = useLayoutContext();
  const apiRef = useGridApiRef();

  const [transactionChanges, setTransactionChanges] = useState<
    Map<
      string,
      Array<{
        transactionTypeId: string;
        operationType: TransactionOperation;
      }>
    >
  >(new Map());
  const pagination = usePagination({
    id: pageId,
    order: 'desc',
    orderBy: 'transactionDate',
  });
  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const { hasEqualisationInDraftState } = useEqualisations(vehicle?._id ?? '');
  const confirmDialog = useDisclosure();

  const { data, isFetching } = api.unrealisedFxGainOrLoss.list.useQuery({
    vehicleId,
    viewDate,
    page: pagination.page,
    rowsPerPage: pagination.rowsPerPage,
    order: pagination.order,
    orderBy: pagination.orderBy,
    ...(currencyRateOverrides
      ? { currencyRateOverrides: Object.fromEntries(currencyRateOverrides) }
      : {}),
  });

  useEffect(() => {
    if (!data?.viewDateCurrencyRates) return;
    if (data?.lastPostingDate) {
      onLastPostingDateLoaded(data.lastPostingDate);
    }

    onCurrenciesLoaded(data.viewDateCurrencyRates);
  }, [data?.viewDateCurrencyRates, onCurrenciesLoaded]);

  const mutationOpts = useHandleTrpcMutation();
  const { mutate: createUnrealisedFXGains, isPending } =
    api.unrealisedFxGainOrLoss.create.useMutation(
      mutationOpts({
        successMessage: 'Created unrealised FX gains/losses successfully.',
        successRedirect: POST_UNREALISED_FX_GAINS_SUCCESS,
      }),
    );

  const postUnrealisedFXGains = () => {
    const postUnrealisedFXGainsBody = rowsForTable.map((row) => {
      const transactions = transactionChanges.get(row.id);
      return {
        sourceTransactionId: row.id,
        transactions: transactions ?? row.transactions,
      };
    });

    createUnrealisedFXGains({
      vehicleId,
      transactions: postUnrealisedFXGainsBody,
      viewDate,
      currencyRateOverrides: Object.fromEntries(currencyRateOverrides),
    });
  };

  const handleTransactionChange = (
    rowId: string,
    newTransactions: Array<{
      transactionTypeId: string;
      operationType: TransactionOperation;
    }>,
  ) => {
    setTransactionChanges((prev) => {
      const newMap = new Map(prev);
      newMap.set(rowId, newTransactions);
      return newMap;
    });

    apiRef.current.updateRows([
      {
        id: rowId,
        transactions: newTransactions,
      },
    ]);
  };

  const rowsForTable = data?.transactions.results ?? [];

  const columns: Array<Column> = [
    {
      field: 'actionRef',
      headerName: 'Action',
      renderCell: ({ row }) => ActionRefLabels[row.actionRef],
      flex: 1,
      sortable: false,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      sortable: false,
    },
    {
      field: 'transactionDate',
      headerName: 'Date',
      renderCell: ({ row }) => row.transactionDate.toLabel(),
      flex: 1,
    },
    {
      field: 'counterpartyLabel',
      headerName: 'Counterparty',
      flex: 1,
      sortable: false,
    },
    {
      field: 'unreconciledAmountTcy',
      headerName: 'Original value (TCY)',
      renderCell: ({ row }) =>
        numberToCurrencyString(
          row.calculationBreakdown?.unreconciledAmountTcy ??
            row.unreconciledAmountTcy,
          {
            fallback: '-',
            currency: row.currencyCode,
          },
        ),
      flex: 1,
      sortable: false,
    },
    {
      field: 'originalResidualValueLcy',
      headerName: 'Original value (LCY)',
      renderCell: ({ row }) =>
        numberToCurrencyString(
          row.calculationBreakdown?.originalResidualValueLcy,
          {
            fallback: '-',
            currency: vehicle?.baseCurrency,
          },
        ),
      flex: 1,
      sortable: false,
    },
    {
      field: 'runningTotalUnrealisedLcy',
      headerName: 'Running total unrealised',
      renderCell: ({ row }) =>
        numberToCurrencyString(
          row.calculationBreakdown?.runningTotalUnrealisedLcy,
          {
            fallback: '-',
            currency: vehicle?.baseCurrency,
            negativeInBrackets: true,
          },
        ),
      flex: 1,
      sortable: false,
    },
    {
      field: 'currentValueLcy',
      headerName: 'Current value (LCY)',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.calculationBreakdown?.currentValueLcy, {
          fallback: '-',
          currency: vehicle?.baseCurrency,
        }),
      flex: 1,
      sortable: false,
    },
    {
      field: 'unrealisedGainOrLossLcy',
      headerName: 'Unrealised gain/loss (LCY)',
      renderCell: ({ row }) => {
        // Flip sign for display if it's a loss type
        const isLoss = row.gainOrLossSubType?.includes('Loss');
        const displayValue =
          isLoss && row.unrealisedGainOrLossLcy > 0
            ? -row.unrealisedGainOrLossLcy
            : row.unrealisedGainOrLossLcy;

        return numberToCurrencyString(displayValue, {
          fallback: '-',
          currency: vehicle?.baseCurrency,
          negativeInBrackets: true,
        });
      },
      flex: 1,
      sortable: false,
    },
  ];

  if (!vehicle) {
    return null;
  }

  return (
    <Spacing>
      <InteractiveTable
        apiRef={apiRef}
        id={pageId}
        columns={columns}
        rows={rowsForTable}
        pagination={pagination}
        scrollable
        loading={isFetching}
        getExpandedContent={(row) => (
          <>
            <Row fullWidth>
              {!transactionTypesLoading && (
                <TransactionAllocator
                  transactionTypeOptions={transactionTypeOptions}
                  transactions={row.transactions}
                  onChange={(newTransactions) => {
                    handleTransactionChange(row.id ?? '', newTransactions);
                  }}
                  transactionCurrency={row.currencyCode}
                  vehicle={vehicle}
                  fxRate={row.currencyRate}
                />
              )}
            </Row>
            <Row fullWidth>
              <ReviewPayments
                transactionId={row.transactions[0]?.id}
                vehicleId={vehicleId}
                currencyCode={row.currencyCode}
                amountTcy={row.amountTcy}
              />
            </Row>
          </>
        )}
      />
      <ButtonGroup>
        <Button
          variant="contained"
          color="primary"
          onClick={
            hasEqualisationInDraftState
              ? confirmDialog.onOpen
              : postUnrealisedFXGains
          }
          disabled={!viewDate || isPending}
        >
          Confirm and proceed
        </Button>
      </ButtonGroup>
      <PayablesOrReceivablesHistory vehicleId={vehicleId} />
      {hasEqualisationInDraftState && (
        <PendingDraftEqualisationsModal
          isOpen={confirmDialog.isOpen}
          onClose={confirmDialog.onClose}
          onConfirm={postUnrealisedFXGains}
          loading={isPending}
        />
      )}
    </Spacing>
  );
};

export default PayablesOrReceivables;
